import {
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Input,
  List,
  Row,
  Typography,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { FormTabKeys } from './DataFeedFormTabs';
import { DataFeedForm, DataFeedFormField, DatasetLabels } from './DataFeedForm';
import {
  APIFiltersConfirmation,
  JSONRequestFiltersConfirmation,
  SearchConfigFiltersConfirmation,
} from './FiltersConfirmation';
import { DataFeedFileTypeLabels } from './DataFeedFiles';
import { OptionalColumnsSummary } from './OptionalColumnsSummary';

const { Text } = Typography;
const { Panel } = Collapse;

export const DataFeedConfirmationTabForm = ({
  setActiveTab,
  teamName,
}: {
  setActiveTab: (key: FormTabKeys) => void;
  teamName: string;
}) => {
  const { getFieldsValue, setFieldValue } = Form.useFormInstance();
  // `true` also returns values set via setFieldValue that have no rendered Form.Item.
  const formValues: DataFeedForm = getFieldsValue(true);
  const handleActiveTab = (tab: FormTabKeys) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    const date = new Date();
    const formattedDate = `${String(date.getFullYear())}${String(
      date.getMonth() + 1
    ).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}${String(
      date.getHours()
    ).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(
      date.getSeconds()
    ).padStart(2, '0')}`;
    setFieldValue(
      DataFeedFormField.dataFeedName,
      `${teamName.replace(
        ' ',
        '_'
      )}-${formValues?.dataset.toLowerCase()}-${formattedDate}`
    );
  }, []);

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            shouldUpdate
            name={DataFeedFormField.dataFeedName}
            label="Data Feed Name"
            required
            rules={[
              {
                type: 'string',
                message: 'Please enter a valid name for the Data Feed',
              },
            ]}
          >
            <Input placeholder="Add a name for the Data Feed" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card
            title={
              <span>
                <Text>Dataset</Text>
                <Button
                  type="link"
                  onClick={() => handleActiveTab('dataset')}
                  className="ml-2"
                >
                  <EditOutlined />
                </Button>
              </span>
            }
            className="mb-4"
          >
            <List
              header={
                <Text strong>
                  {DatasetLabels[formValues?.dataset]}{' '}
                  {formValues?.deltas ? (
                    <span className="font-bold">(full and delta files)</span>
                  ) : (
                    <span className="font-bold">(full files only)</span>
                  )}
                </Text>
              }
              dataSource={formValues?.files}
              renderItem={(item) => (
                <List.Item>
                  <div>
                    <div>{DataFeedFileTypeLabels[item]}</div>
                    <OptionalColumnsSummary
                      fileType={item}
                      columns={formValues?.optionalColumns?.[item]}
                    />
                  </div>
                </List.Item>
              )}
            />
          </Card>

          <Card
            title={
              <span>
                Destination
                <Button
                  type="link"
                  onClick={() => handleActiveTab('destination')}
                  className="ml-2"
                >
                  <EditOutlined />
                </Button>
              </span>
            }
            className="mb-4"
          >
            <Text>Type: {formValues?.destination?.type?.label}</Text>
            <br />
            <Text>
              Keep only latest file(s):{' '}
              {formValues?.destination?.keepOnlyLatest ? 'Yes' : 'No'}
            </Text>
          </Card>

          <Card
            title={
              <span>
                Frequency
                <Button
                  type="link"
                  onClick={() => handleActiveTab('frequency')}
                  className="ml-2"
                >
                  <EditOutlined />
                </Button>
              </span>
            }
          >
            <Text>{formValues?.frequency?.label}</Text>
          </Card>
        </Col>
        <Col span={12}>
          <Collapse defaultActiveKey={['1']} bordered={false} className="mb-4">
            <Panel header="Filters" key="1">
              {formValues?.filters &&
                formValues?.filters[formValues.dataset] && (
                  <APIFiltersConfirmation formValues={formValues} />
                )}
              {formValues?.filters?.searchConfig && (
                <SearchConfigFiltersConfirmation formValues={formValues} />
              )}
              {formValues?.filters?.jsonRequest && (
                <JSONRequestFiltersConfirmation
                  jsonRequest={formValues.filters.jsonRequest}
                />
              )}
            </Panel>
          </Collapse>
        </Col>
      </Row>
    </>
  );
};
